import { type KeyboardEvent, type RefObject, useEffect, useMemo, useState } from 'react';
import type { GlobalSearchResults } from '#shared/types';
import { useCan } from '#client/hooks/useCan';
import { useDebounced } from '#client/hooks/useDebounced';
import { useKeyedRequest } from '#client/hooks/useKeyedRequest';
import { api } from '#client/lib/api';
import { ENDPOINT } from '#client/lib/endpoints';
import { readRecent } from '#client/lib/recent';
import { useAuth } from '#client/providers/AuthProvider';
import {
  type PaletteRow,
  buildPaletteRows,
  clampActiveIndex,
  isNavigationKey,
  isSearchable,
  nextActiveIndex,
} from './palette.logic';

/** The rows for a (trimmed) query: search hits, recents and matching commands. */
export function usePaletteRows(query: string): PaletteRow[] {
  const can = useCan();
  const { user } = useAuth();

  const debouncedQuery = useDebounced(query, 200);
  const search = useKeyedRequest<GlobalSearchResults>(
    isSearchable(debouncedQuery) ? debouncedQuery : undefined,
    (signal) => api.get<GlobalSearchResults>(ENDPOINT.search, { q: debouncedQuery }, signal),
    'Search is unavailable',
  );

  /*
   * Results are *used* only once the query is long enough, rather than being
   * cleared when it is not. Same behaviour, no state write, and stale results
   * can never flash while a new query is in flight.
   */
  const results = isSearchable(query) ? search.data : undefined;

  // Read once per open, which is once per mount.
  const recent = useMemo(() => readRecent(user?.id), [user?.id]);

  return useMemo(
    () => buildPaletteRows({ results, recent, query, can }),
    [results, recent, query, can],
  );
}

interface PaletteNavigation {
  activeIndex: number;
  activeRow: PaletteRow | undefined;
  setActive: (index: number) => void;
  handleKeyDown: (event: KeyboardEvent) => void;
}

/**
 * The keyboard-driven highlight over the rows, and Enter to open it.
 *
 * `listRef` is the listbox, kept scrolled so the highlighted row stays in view.
 */
export function usePaletteNavigation(
  rows: readonly PaletteRow[],
  onOpen: (href: string) => void,
  listRef: RefObject<HTMLUListElement | null>,
): PaletteNavigation {
  const [active, setActive] = useState(0);

  const activeIndex = clampActiveIndex(active, rows.length);
  const activeRow = rows[activeIndex];

  const handleKeyDown = (event: KeyboardEvent) => {
    const { key } = event;
    if (key === 'Enter') {
      event.preventDefault();
      if (activeRow) onOpen(activeRow.href);
    } else if (isNavigationKey(key)) {
      event.preventDefault();
      setActive((current) => nextActiveIndex(key, current, rows.length) ?? current);
    }
  };

  // Keep the highlighted row in view when arrowing past the fold.
  useEffect(() => {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex, listRef]);

  return { activeIndex, activeRow, setActive, handleKeyDown };
}
